import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DynamicIcon from "../DynamicIcon";
import { selectAccountData } from "../../redux/main/accountSlice";
import { createInternalVehicle, getInternalVehicleDetails, selectInternalVehicleDetails, updateInternalVehicle } from "../../redux/internalVehicle/internalVehicleSlice";
import { resetRefreshNow, selectRightModalmode, setHasToRefresh } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";

export default function AddEditInternalVehicle({ vehicleId }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const vehicleDetails = useSelector(selectInternalVehicleDetails);
    const modalMode = useSelector(selectRightModalmode);
    const token = localStorage.getItem('installamelo-auth-token');
    const [isLoading, setIsLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        model: '',
        plateNumber: '',
        notes: '',
        km: 0,
        hours: 0,
    });

    // Initialize form data based on edit mode
    useEffect(() => {
        if (modalMode === 'edit' && vehicleId) {
            setIsLoading(true);
            dispatch(getInternalVehicleDetails({ vehicleId, token }))
                .then(() => setIsLoading(false))
                .catch(() => {
                    toast.error("Errore nel caricamento dei dettagli del veicolo");
                    setIsLoading(false);
                });
        }
        dispatch(resetRefreshNow());
    }, [dispatch, vehicleId, modalMode, token]);

    // Populate form when vehicleDetails is loaded
    useEffect(() => {
        if (vehicleDetails && modalMode === 'edit') {
            setFormData({
                model: vehicleDetails.model || '',
                plateNumber: vehicleDetails.plateNumber || '',
                notes: vehicleDetails.notes || '',
                km: vehicleDetails.km || 0,
                hours: vehicleDetails.hours || 0,
            });
        }
    }, [vehicleDetails, modalMode]);

    // Form change handler
    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'number' ? Number(value) : value
        }));
    };

    // Form submission handler
    const handleSubmit = (e) => {
        e.preventDefault();
        
        setIsLoading(true);
        
        const vehicleData = {
            ...formData,
            accountId: accountData._id,
            internal: true, // Impostiamo internal a true per i veicoli interni
            creationDate: new Date()
        };
        
        // If we're in edit mode, include the ID
        if (modalMode === 'edit' && vehicleDetails) {
            vehicleData._id = vehicleDetails._id;
            vehicleData.lastUpdateDate = new Date();
        }
        
        const action = modalMode === 'add' 
            ? createInternalVehicle({ vehicle: vehicleData, token })
            : updateInternalVehicle({ vehicle: vehicleData, token });
        
        dispatch(action)
            .then(() => {
                const successMessage = modalMode === 'add' 
                    ? "Veicolo interno creato con successo" 
                    : "Veicolo interno aggiornato con successo";
                toast.success(successMessage);
                dispatch(setHasToRefresh());
                setIsLoading(false);
            })
            .catch((err) => {
                const errorMessage = modalMode === 'add' 
                    ? "Errore nella creazione del veicolo interno" 
                    : "Errore nell'aggiornamento del veicolo interno";
                toast.error(errorMessage);
                setIsLoading(false);
            });
    };

    return (
        <div className="flex flex-col h-full">
            {isLoading && <LoadingSection />}
            
            <div className="flex-1 overflow-auto p-4">
                <h2 className="text-xl font-semibold mb-4">
                    {modalMode === 'add' ? 'Nuovo Veicolo Interno' : 'Modifica Veicolo Interno'}
                </h2>
                
                <form onSubmit={handleSubmit}>
                    {/* Modello */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Modello</label>
                        <input
                            type="text"
                            name="model"
                            value={formData.model}
                            onChange={handleChange}
                            className="mt-1 p-2 w-full border rounded-md"
                            required
                        />
                    </div>
                    
                    {/* Targa */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Targa</label>
                        <input
                            type="text"
                            name="plateNumber"
                            value={formData.plateNumber}
                            onChange={handleChange}
                            className="mt-1 p-2 w-full border rounded-md"
                        />
                    </div>
                    
                    {/* Note */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Note</label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            className="mt-1 p-2 w-full border rounded-md"
                            rows="3"
                        />
                    </div>
                    
                    {/* Chilometri */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Chilometri</label>
                        <input
                            type="number"
                            name="km"
                            value={formData.km}
                            onChange={handleChange}
                            className="mt-1 p-2 w-full border rounded-md"
                            min="0"
                        />
                    </div>
                    
                    {/* Ore di utilizzo */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Ore di utilizzo</label>
                        <input
                            type="number"
                            name="hours"
                            value={formData.hours}
                            onChange={handleChange}
                            className="mt-1 p-2 w-full border rounded-md"
                            min="0"
                        />
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            type="submit"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={isLoading}
                        >
                            <DynamicIcon iconClass="fa-solid fa-save mr-2" />
                            {modalMode === 'add' ? 'Crea' : 'Aggiorna'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
